import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { ClassesApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";

export const useClassDelete = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (classId: number) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      const service = createAuthenticatedApi(ClassesApi, accessToken);

      return service.apiClassesIdDelete({
        id: classId,
      });
    },
    onSuccess: async () => {
      // Invalidate both public and admin classes queries, and individual class queries
      await queryClient.invalidateQueries({
        predicate: (query) => {
          return (
            Array.isArray(query.queryKey) &&
            (query.queryKey[0] === QueryKeys.Classes ||
              query.queryKey[0] === QueryKeys.Class)
          );
        },
      });
      toast({
        title: "Class deleted",
        description: "Class deleted successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to delete class",
      );

      toast({
        title: "Cannot delete class",
        description: errorMessage,
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};

